import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { AdminUser } from '../../domain/entities/admin-user.entity'
import type { UserRole } from '@/core/enums/user-role.enum'
import { LocalStorageService } from '@/services/storage/local-storage.service'
import axiosInstance from '@/services/api/axios.config'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AdminUser | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => !!user.value && !!accessToken.value)
  const userRole = computed(() => user.value?.role)
  const userName = computed(() => user.value?.name)

  const storage = new LocalStorageService()

  async function login(email: string, password: string): Promise<void> {
    try {
      isLoading.value = true
      error.value = null

      const response = await axiosInstance.post('/auth/admin/login/', { email, password })
      const data = response.data

      const adminUser = new AdminUser(
        data.user.id,
        data.user.email,
        data.user.name,
        data.user.role,
        data.user.is_active,
        data.user.avatar,
        data.user.last_login ? new Date(data.user.last_login) : undefined,
        new Date(data.user.created_at)
      )

      user.value = adminUser
      accessToken.value = data.access_token
      refreshToken.value = data.refresh_token

      storage.setUser(data.user)
      storage.setTokens(data.access_token, data.refresh_token)
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Login failed. Please check your credentials.'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      await axiosInstance.post('/auth/logout/')
    } catch {
      // Ignore logout errors
    } finally {
      user.value = null
      accessToken.value = null
      refreshToken.value = null
      storage.clearAuth()
    }
  }

  function initializeAuth(): void {
    const storedUser = storage.getUser()
    const storedTokens = storage.getTokens()

    if (storedUser && storedTokens.accessToken) {
      user.value = new AdminUser(
        storedUser.id,
        storedUser.email,
        storedUser.name,
        storedUser.role,
        storedUser.is_active,
        storedUser.avatar,
        storedUser.last_login ? new Date(storedUser.last_login) : undefined,
        new Date(storedUser.created_at)
      )
      accessToken.value = storedTokens.accessToken
      refreshToken.value = storedTokens.refreshToken
    }
  }

  function hasPermission(requiredRole: UserRole): boolean {
    if (!user.value) return false
    return user.value.canPerform(requiredRole)
  }

  async function refreshAccessToken(): Promise<boolean> {
    const tokens = storage.getTokens()
    if (!tokens.refreshToken) return false

    try {
      const response = await axiosInstance.post('/auth/token/refresh/', {
        refresh: tokens.refreshToken
      })
      accessToken.value = response.data.access
      storage.setTokens(response.data.access, tokens.refreshToken)
      return true
    } catch {
      logout()
      return false
    }
  }

  return {
    user,
    accessToken,
    refreshToken,
    isLoading,
    error,
    isAuthenticated,
    userRole,
    userName,
    login,
    logout,
    initializeAuth,
    hasPermission,
    refreshAccessToken,
  }
})
